/**
 * Turns raw form input into the value a settings bag should hold for a given
 * property descriptor, and reads possibly malformed stored values back safely.
 */
import type { ListEntry, PropertyDescriptor, SelectOption } from '../../../core/definitions';
import type { SettingsBag } from '../../../core/model';

export interface Rgba {
  r: number;
  g: number;
  b: number;
  /** 0..1 */
  a: number;
}

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export function parseHexColor(text: unknown): Rgba | null {
  if (typeof text !== 'string') return null;
  const match = HEX.exec(text.trim());
  if (!match) return null;
  let hex = match[1]!;
  if (hex.length <= 4) hex = [...hex].map((c) => c + c).join('');
  const byte = (i: number): number => parseInt(hex.slice(i, i + 2), 16);
  return { r: byte(0), g: byte(2), b: byte(4), a: hex.length === 8 ? byte(6) / 255 : 1 };
}

const hex2 = (value: number): string => Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, '0');

export function toHexColor(color: Rgba, withAlpha: boolean): string {
  const base = `#${hex2(color.r)}${hex2(color.g)}${hex2(color.b)}`;
  return withAlpha ? base + hex2(color.a * 255) : base;
}

const FUNCTIONAL = /^(rgba?|hsla?)\(\s*([^)]*)\)$/i;

/** A CSS number or percentage; percentages scale to `full`. */
function cssNumber(text: string, full: number): number | null {
  const trimmed = text.trim();
  const percent = trimmed.endsWith('%');
  const value = Number(percent ? trimmed.slice(0, -1) : trimmed.replace(/deg$/i, ''));
  if (trimmed === '' || !Number.isFinite(value)) return null;
  return percent ? (value / 100) * full : value;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const hue = (((h % 360) + 360) % 360) / 360;
  const k = (n: number): number => (n + hue * 12) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): number => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0) * 255, f(8) * 255, f(4) * 255];
}

/**
 * Any colour text a user is likely to paste: hex, or `rgb()`, `rgba()`,
 * `hsl()`, `hsla()` with comma or space syntax (an optional `/ alpha`).
 */
export function parseCssColor(text: unknown): Rgba | null {
  if (typeof text !== 'string') return null;
  const hex = parseHexColor(text);
  if (hex) return hex;
  const match = FUNCTIONAL.exec(text.trim());
  if (!match) return null;
  const parts = match[2]!.split(/\s*[,/]\s*|\s+/).filter(Boolean);
  if (parts.length < 3 || parts.length > 4) return null;
  const alpha = parts[3] === undefined ? 1 : cssNumber(parts[3], 1);
  if (alpha === null) return null;
  const clampAlpha = Math.min(1, Math.max(0, alpha));
  if (match[1]!.toLowerCase().startsWith('rgb')) {
    const [r, g, b] = parts.slice(0, 3).map((p) => cssNumber(p, 255));
    if (r === null || g === null || b === null || r === undefined || g === undefined || b === undefined) return null;
    const clamp = (v: number): number => Math.min(255, Math.max(0, v));
    return { r: clamp(r), g: clamp(g), b: clamp(b), a: clampAlpha };
  }
  const h = cssNumber(parts[0]!, 360);
  const sat = cssNumber(parts[1]!, 1);
  const light = cssNumber(parts[2]!, 1);
  if (h === null || sat === null || light === null || !parts[1]!.endsWith('%') || !parts[2]!.endsWith('%')) return null;
  const [r, g, b] = hslToRgb(h, Math.min(1, Math.max(0, sat)), Math.min(1, Math.max(0, light)));
  return { r, g, b, a: clampAlpha };
}

/** Canonical '#rrggbb' / '#rrggbbaa' for a descriptor, falling back to opaque black. */
export function coerceColor(raw: unknown, alpha: boolean): string {
  return toHexColor(parseCssColor(raw) ?? { r: 0, g: 0, b: 0, a: 1 }, alpha);
}

let entrySerial = 0;

/** A fresh, practically unique id for a list entry. */
export function newEntryId(): string {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID().slice(0, 12) : Math.random().toString(36).slice(2, 14);
  return `${random}-${(++entrySerial).toString(36)}`;
}

/**
 * Stored list value as entries with unique ids. Tolerates the legacy shape (a
 * plain string array) and repairs missing or repeated ids, so rows can always
 * be keyed and removed one at a time.
 */
export function coerceList(raw: unknown): ListEntry[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: ListEntry[] = [];
  for (const item of raw) {
    let entry: ListEntry;
    if (typeof item === 'string') entry = { id: '', value: item };
    else if (item && typeof item === 'object' && !Array.isArray(item)) {
      const bag = item as Record<string, unknown>;
      entry = { ...bag, id: typeof bag.id === 'string' ? bag.id : '', value: typeof bag.value === 'string' ? bag.value : String(bag.value ?? '') };
    } else continue;
    if (!entry.id || seen.has(entry.id)) entry.id = newEntryId();
    seen.add(entry.id);
    out.push(entry);
  }
  return out;
}

/** Whole milliseconds within the descriptor range. */
export function coerceDuration(raw: unknown, desc: { min?: number; max?: number }): number {
  let value = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(value)) value = desc.min ?? 0;
  value = Math.round(value);
  if (desc.min !== undefined && value < desc.min) value = desc.min;
  if (desc.max !== undefined && value > desc.max) value = desc.max;
  return Math.max(0, value);
}

export interface DurationParts {
  hours: number;
  minutes: number;
  /** May carry a fraction (milliseconds). */
  seconds: number;
}

export function splitDuration(ms: number): DurationParts {
  const total = Math.max(0, Math.round(ms));
  const hours = Math.floor(total / 3_600_000);
  const minutes = Math.floor((total % 3_600_000) / 60_000);
  const seconds = (total % 60_000) / 1000;
  return { hours, minutes, seconds };
}

export function joinDuration(parts: DurationParts): number {
  return Math.round((parts.hours * 3600 + parts.minutes * 60 + parts.seconds) * 1000);
}

const pad = (n: number): string => String(Math.trunc(Math.abs(n))).padStart(2, '0');

/** '+02:00' for an offset in minutes east of UTC (the negation of Date.getTimezoneOffset()). */
export function formatOffset(minutesEast: number): string {
  return `${minutesEast < 0 ? '-' : '+'}${pad(minutesEast / 60)}:${pad(minutesEast % 60)}`;
}

/** 'YYYY-MM-DDTHH:MM:SS' as shown by <input type="datetime-local">, in this browser's time zone. */
export function isoToLocalInput(iso: unknown): string {
  if (typeof iso !== 'string' || iso === '') return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** A datetime-local value read as this browser's wall time, stored as ISO-8601 with an explicit offset. */
export function localInputToIso(local: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(local);
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0));
  if (Number.isNaN(date.getTime())) return null;
  return `${y}-${mo}-${d}T${h}:${mi}:${pad(Number(s ?? 0))}${formatOffset(-date.getTimezoneOffset())}`;
}

/** The id the guest picker stores for a guest: the stable participant when known. */
export function guestKey(guest: { guestId: string; participantId?: string }): string {
  return guest.participantId ?? guest.guestId;
}

export function coerceNumber(raw: unknown, desc: { min?: number; max?: number; step?: number }, fallback = 0): number {
  let value = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw.replace(',', '.')) : NaN;
  if (!Number.isFinite(value)) value = fallback;
  if (desc.step && desc.step > 0) {
    const origin = desc.min ?? 0;
    value = origin + Math.round((value - origin) / desc.step) * desc.step;
    value = Number(value.toFixed(6));
  }
  if (desc.min !== undefined && value < desc.min) value = desc.min;
  if (desc.max !== undefined && value > desc.max) value = desc.max;
  return value;
}

/** Maps a <select> string back to the option's own value type (numbers stay numbers). */
export function coerceSelect(raw: unknown, options: readonly SelectOption[]): string | number {
  const match = options.find((o) => String(o.value) === String(raw));
  if (match) return match.value;
  return typeof raw === 'number' ? raw : String(raw ?? '');
}

export function coerceBoolean(raw: unknown): boolean {
  if (typeof raw === 'string') return raw === 'true' || raw === '1' || raw === 'on';
  return Boolean(raw);
}

/** The value to store for `desc` given raw UI input. */
export function coerceValue(desc: PropertyDescriptor, raw: unknown, options?: readonly SelectOption[]): unknown {
  switch (desc.type) {
    case 'number':
      return coerceNumber(raw, desc);
    case 'boolean':
      return coerceBoolean(raw);
    case 'select':
      return coerceSelect(raw, options ?? (Array.isArray(desc.options) ? desc.options : []));
    case 'color':
      return coerceColor(raw, desc.alpha === true);
    case 'asset':
      if (desc.multiple) return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : typeof raw === 'string' && raw ? [raw] : [];
      return Array.isArray(raw) ? String(raw[0] ?? '') : String(raw ?? '');
    case 'list':
      return coerceList(raw);
    case 'duration':
      return coerceDuration(raw, desc);
    case 'button':
    case 'info':
      return undefined;
    default:
      return raw === null || raw === undefined ? '' : String(raw);
  }
}

/** Current value of a property as the matching field expects it, tolerating bad stored data. */
export function readValue(desc: PropertyDescriptor, settings: SettingsBag): unknown {
  const stored = settings[desc.key];
  switch (desc.type) {
    case 'number':
      return typeof stored === 'number' && Number.isFinite(stored) ? stored : coerceNumber(stored, {}, desc.min ?? 0);
    case 'boolean':
      return coerceBoolean(stored);
    case 'select':
      return stored === undefined || stored === null ? '' : (stored as string | number);
    case 'color':
      return coerceColor(stored, desc.alpha === true);
    case 'asset':
    case 'list':
    case 'duration':
      return coerceValue(desc, stored);
    case 'button':
    case 'info':
      return undefined;
    default:
      return stored === null || stored === undefined ? '' : String(stored);
  }
}

/** Consecutive descriptors sharing a `section`, in order. Descriptors without one form untitled runs. */
export function groupSections(descriptors: readonly PropertyDescriptor[]): { title: string | null; items: PropertyDescriptor[] }[] {
  const groups: { title: string | null; items: PropertyDescriptor[] }[] = [];
  for (const desc of descriptors) {
    const title = desc.section ?? null;
    const last = groups[groups.length - 1];
    if (last && last.title === title) last.items.push(desc);
    else groups.push({ title, items: [desc] });
  }
  return groups;
}

/** Descriptors whose `visibleWhen` passes; a throwing predicate keeps the property visible. */
export function visibleDescriptors(descriptors: readonly PropertyDescriptor[], settings: SettingsBag): PropertyDescriptor[] {
  return descriptors.filter((desc) => {
    if (!desc.visibleWhen) return true;
    try {
      return desc.visibleWhen(settings) !== false;
    } catch {
      return true;
    }
  });
}

/** Keys whose values differ between two bags (shallow, JSON comparison for nested values). */
export function changedKeys(before: SettingsBag, after: SettingsBag): string[] {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...keys].filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]));
}

/** Patch that takes `current` back to `captured`; keys added since the capture are sent as undefined. */
export function restorePatch(captured: SettingsBag, current: SettingsBag): SettingsBag {
  const patch: SettingsBag = {};
  for (const key of changedKeys(captured, current)) patch[key] = key in captured ? captured[key] : undefined;
  return patch;
}
